import { datadogRum } from "@datadog/browser-rum";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import FirebaseClientUtil from "@yardzen-inc/firebase-client-util";
import { TrafficTypes } from "@yardzen-inc/react-split";
import { getAuth } from "firebase/auth";
import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { AppRouter } from "./components";
import { ScrollToTop } from "./components/ScrollToTop";
import { SplitAlertBannerByTrafficType } from "./components/SplitAlertBannerByTrafficType";
import { CrashPage } from "./pages/CrashPage";
import { store } from "./store";
import { Providers } from "./util";
import { forceLogoutWhenInconsistentUserDetected } from "./util/cookies/methods";
import { ErrorBoundary } from "./util/errors/ErrorBoundary";
import { getStripeKey } from "./util/functions/getStripeKey";
import { useLogRocket } from "./util/hooks/useLogRocket";
import "./util/newrelic";
import { segment } from "./util/Segment";
import { ANON_DISPLAY_BANNER } from "./util/Split/splitTreatments";

const stripePromise = loadStripe(getStripeKey());

let persistor = persistStore(store);

forceLogoutWhenInconsistentUserDetected();
FirebaseClientUtil.setUserClaimListener();

const App: React.FC = () => {
  useEffect(() => {
    if (!datadogRum.getInternalContext()) {
      datadogRum.init({
        applicationId: "85dc5232-2f23-40db-96b4-e2ff39c2df6f",
        clientToken: "pub3a41b898b58be888681bd1763723c3a6",
        site: "us5.datadoghq.com",
        service: "client-dashboard",
        env: process.env.NODE_ENV,
        sessionSampleRate: 100,
        sessionReplaySampleRate: 20,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: "mask-user-input",
      });
      datadogRum.startSessionReplayRecording();
    }
  }, []);

  useEffect(() => {
    const auth = getAuth();
    return auth.onAuthStateChanged(user => {
      if (user) {
        segment.setUserId(user.uid);
        segment.identify({
          email: user?.email ?? undefined,
        });
      }
    });
  }, []);

  useLogRocket();
  return (
    <>
      <ErrorBoundary
        onError={err => {
          try {
            window?.newrelic?.noticeError(err, {
              "ErrorBoundary Exception": 1,
            });
          } catch (error) {
            console.error(error);
          }
        }}
        ErrorPageComponent={CrashPage}
      >
        <Providers>
          <Provider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <Elements stripe={stripePromise}>
                <SplitAlertBannerByTrafficType
                  splitTreatment={ANON_DISPLAY_BANNER}
                  trafficType={TrafficTypes.ANONYMOUS}
                />
                <ScrollToTop />
                <AppRouter />
              </Elements>
            </PersistGate>
          </Provider>
        </Providers>
      </ErrorBoundary>
    </>
  );
};

export default App;
